/* eslint-disable max-classes-per-file */
import {mix, Eventable} from '../mixin';
import userAgent from './userAgent';
import {roundFractions} from '../transform/number';
import {join} from '../transform/object';

const {
    window, document, Math, parseInt, setTimeout
} = globalThis;
const {body, head} = document;

const metaViewportNode = (function()
{
    const {children} = head;
    let i = children.length;

    while (i--)
    {
        if (children[i].tagName === 'META' &&
            children[i].getAttribute('name') === 'viewport')
        {
            return children[i];
        }
    }

    const node = document.createElement('meta');
    node.setAttribute('name', 'viewport');

    return node;
}());

let enabled = true;
let windowLoaded = false;

/**
 * @mixes Eventable
 * @hideconstructor
 * @see responder.createViewportResizer
 * @fires ViewportResizer#event:resize
 */
class ViewportResizer extends mix().with(Eventable)
{
    #handler = null;
    #started = false;
    #blocked = false;
    #handling = false;
    #resetTime = 300;
    #overwriteParams = null;

    constructor(handler, overwriteParams = {})
    {
        super();
        this.#overwriteParams = overwriteParams;
        this.#handler = handler;
        if (userAgent.mobile.ios)
        {
            this.#resetTime = 500;
        }
        if (userAgent.client.safari <= 12)
        {
            this.#resetTime = 800;
        }
    }

    /**
     * @type {number} Wartezeit in Millisekunden zwischen {@link responder.resetViewport Viewport-Reset} und
     * Handler/Event. Auf iOS standardmäßig etwas länger.
     */
    set resetTime(time)
    {
        this.#resetTime = time;
    }

    /**
     * Beginne den Viewport-Resize, z.B. nach einem Orientation-Change.
     */
    start()
    {
        if (!this.#blocked && !this.#started)
        {
            this.#started = true;
            this._block(100, () =>
            {
                this.#handling = true;
                responder.resetViewport(this.#overwriteParams);
                this._block(this.#resetTime, () => this._resize());
            });
        }
    }

    _resize()
    {
        if (this.#blocked || !this.#started) { return; }

        if (this.#handling)
        {
            this.#handling = false;
            this.#handler.call(responder);
            /**
             * @event ViewportResizer#resize
             */
            this.fire('resize');

            // Cooldown
            this._block(200);
            this.#started = false;
        }
    }

    _block(ms, callback = () => {})
    {
        if (this.#blocked) { return; }
        this.#blocked = true;
        setTimeout(() =>
        {
            this.#blocked = false;
            callback();
        }, ms);
    }
}

const resizeHandler = function(evt)
{
    if (!enabled) { return; }

    const type = evt === undefined ? 'resize' : evt.type;

    /**
     * Wird bei jedem resize des Browsers gefeuert.
     * @event responder#resize
     * @property {number} bodyWidth
     * @property {number} windowWidth
     * @property {string} originalType
     */
    responder.fire('resize', { // eslint-disable-line
        bodyWidth: responder.bodyWidth, // eslint-disable-line
        windowWidth: responder.windowWidth, // eslint-disable-line
        originalType: type
    });
};

/**
 * @namespace responder
 * @mixes Eventable
 */
const methods = /** @lends responder */
{
    /** Getter
     * @type {number}
     */
    get bodyWidth()
    {
        return body.offsetWidth;
    },

    /** Getter
     * @type {number}
     */
    get windowWidth()
    {
        return window.innerWidth;
    },

    getFullScreenSize()
    {
        console.warn('Please use the method "responder::getFullCssScreenSize"!');
        return this.getFullCssScreenSize();
    },

    getVisibleScreenSize()
    {
        console.warn('Please use the method "responder::getAvailableCssScreenSize"!');
        return this.getAvailableCssScreenSize();
    },

    /** Diese Funktion gibt einfach nur die ``window.screen`` Werte weiter - bereinigt um den Rotationsbug von iOS.
     * Es handelt sich hierbei um die CSS-Pixel, nicht die physischen Pixel des Bildschirms.
     */
    getFullCssScreenSize()
    {
        // http://tripleodeon.com/2011/12/first-understand-your-screen/
        const {screen} = window;

        let {height, width} = screen;

        if (userAgent.mobile.ios && Math.abs(window.orientation) === 90)
        {
            height = screen.width;
            width = screen.height;
        }

        return {width, height};
    },

    /** Diese Funktion berechnet die CSS-Pixel bereinigt um Browser-Bars etc. Dafür wird temporär stark in das
     * Grundlayout der Seite eingegriffen (Styles von ``body`` & ``html``). Diese Änderungen
     * werden wieder rückgängig gemacht. Funktioniert nur verlässlich, wenn die Weite des Viewports auf
     * ``device-width`` und das Scale auf 1 gestellt ist.
     */
    getAvailableCssScreenSize()
    {
        const html = body.parentNode;
        const cachedValues = {
            bodyHeight: body.style.height,
            bodyOverflow: body.style.overflow,
            htmlHeight: html.style.height,
            htmlOverflow: html.style.overflow
        };

        body.style.height = '100%';
        body.style.overflow = 'hidden';
        html.style.height = '100%';
        html.style.overflow = 'hidden';

        const htmlStyle = window.getComputedStyle(html);

        const visibleScreen = {
            width: parseInt(htmlStyle.width),
            height: parseInt(htmlStyle.height)
        };

        body.style.height = cachedValues.bodyHeight;
        body.style.overflow = cachedValues.bodyOverflow;
        html.style.height = cachedValues.htmlHeight;
        html.style.overflow = cachedValues.htmlOverflow;

        const ratio = visibleScreen.width / visibleScreen.height;

        if (!visibleScreen.height || !visibleScreen.width ||
            (userAgent.mobile.general && (ratio > 3 || ratio < 0.3)))
        {
            return this.getFullScreenSize();
        }

        return visibleScreen;
    },

    /**  */
    getScreenOrientation()
    {
        const orientation =
            window.screen.orientation ||
            window.screen.mozOrientation ||
            window.screen.msOrientation;

        if (typeof orientation === 'string')
        {
            return (orientation.indexOf('landscape') === 0 ? 'landscape' : 'portrait');
        }

        if (typeof window.matchMedia === 'function')
        {
            return window.matchMedia('(orientation: landscape)').matches ?
                'landscape' : 'portrait';
        }
        const fullScreen = this.getFullScreenSize();

        return (fullScreen.width > fullScreen.height ? 'landscape' : 'portrait');
    },

    /** Generiert einen Meta-Viewport-Tag, der eine Seite mit festen Maßen so skaliert, dass sie
     * sichtbar bleibt. Der Tag sollte dafür in einem resetteten Zustand sein. Wenn man die Werte nicht
     * anhand der Fullscreen-Bildschirm-Maße berechnet, wird ``responder::getAvailableCssScreenSize``
     * aufgerufen. Die Skalierung wird gerne gecacht - bei Problemen sollte der Browser und/oder
     * die Kompilierung neu gestartet wird.
     * @param {Object} options
     * @param {number} options.siteWidth Weite des Bereichs in Pixeln, der sichtbar sein soll
     * @param {number=} options.siteHeight Höhe des Bereichs in Pixeln, der sichtbar sein soll
     * @param {boolean=} options.fullScreen Soll die Skalierung anhand der Fullscreen-Bildschirm-Maße berechnet werden?
     * @param {boolean=} options.upScalable Soll User Seite nach oben skalieren können
     * @param {number=} options.saveFrameWidth die Skalierung darf die Seite nicht breiter skalieren als diese Breite.
     * Ohne Angabe wird Seite so klein skaliert, dass sie komplett ins Bild passt (Ränder möglich)
     * @param {Object=} options.overwriteParams Params für den Viewport-Tag, welche am Ende hinzugefügt werden
     */
    resizeViewport({
        siteWidth, siteHeight, fullScreen, upScalable, saveFrameWidth, overwriteParams = {}
    })
    {
        const content = {
            width: '',
            'initial-scale': ''
        };

        let ratio = '1.0';

        // http://stackoverflow.com/questions/11345896/full-webpage-and-disabled-zoom-viewport-meta-tag-for-all-mobile-browsers

        if (siteWidth !== 'device-width')
        {
            const screen = fullScreen ?
                this.getFullCssScreenSize() : this.getAvailableCssScreenSize();
            let ratio2;

            // Wenn die Höhe egal ist und nur auf die Breite skaliert werden muss
            if (siteHeight === undefined)
            {
                ratio = screen.width / siteWidth;
                ratio = roundFractions(ratio, 3);
            // Wenn die Website auch eine feste Höhe hat und der Screen nicht ausreicht, um kompletten Inhalt darzustellen
            // oder ein SaveFrame angegeben ist
            }
            else if (
                siteHeight > screen.height || siteWidth > screen.width ||
                typeof saveFrameWidth === 'number'
            )
            {
                // Falls die Seite ein Breitformat hat
                if (siteWidth > siteHeight)
                {
                    ratio = screen.width / siteWidth;

                    // Falls die Skalierung anhand der Breite die Seite in der Höhe beschneidet
                    if (ratio * siteHeight >= screen.height)
                    {
                        ratio = screen.height / siteHeight;

                        // Falls die Skalierung anhand der Höhe die Seite noch breiter macht als eine mögliche Saveframe-Breite
                        if (typeof saveFrameWidth === 'number' &&
                            (siteWidth * ratio) > saveFrameWidth)
                        {
                            ratio2 = saveFrameWidth / siteWidth;
                            ratio = ratio2 < ratio ? ratio : ratio2;
                        }
                    }

                // Falls die Seite ein Hochformat hat
                }
                else
                {
                    ratio = screen.height / siteHeight;
                    if (ratio * siteWidth >= screen.width)
                    {
                        ratio = screen.width / siteWidth;
                    }
                }

                ratio = roundFractions(ratio, 3);
            }
            // Wenn die Website auch eine feste Höhe hat und diese kleiner ist als die Screen-Höhe
            else if (siteHeight < screen.height)
            {
                ratio = screen.height / siteHeight;
                if (ratio * siteWidth >= screen.width)
                {
                    ratio = screen.width / siteWidth;
                }
                ratio = roundFractions(ratio, 3);
            }
            else
            {
                content.height = siteHeight;
            }
        }

        content.width = saveFrameWidth || siteWidth;
        content['initial-scale'] = ratio;

        // iOS 10+ interessiert sich nicht mehr für die 'user-scalable' Angabe
        // außerdem geht das Kleinskalieren gar nicht mehr, falls mehr als 'initial-scale' definiert ist ...
        if (upScalable !== undefined)
        {
            content['minimum-scale'] = ratio;
            if (upScalable === false)
            {
                content['maximum-scale'] = ratio;
                content['user-scalable'] = 'no';
            }
            else
            {
                content['user-scalable'] = 'yes';
            }

            if (userAgent.mobile.android)
            {
                content['user-scalable'] = 'yes';
            }
        }

        if (userAgent.mobile.android)
        {
            content['target-densitydpi'] = 'device-dpi';
        }

        Object.assign(content, overwriteParams);

        this.setViewportContent(
            join(content, ', ', '=')
        );

        return ratio;
    },

    /** Setzt den Content des Meta-Viewport-Tags. Ist egal, ob dieser bereits vorhanden ist oder nicht.
     * @param {string} content
     */
    setViewportContent(content)
    {
        metaViewportNode.setAttribute('content', content);
        if (metaViewportNode.parentNode === null)
        {
            head.appendChild(metaViewportNode);
        }
        return this;
    },

    /** Setzt den Content des Meta-Viewport-Tags auf ``width=device-width, initial-scale=1.0``.
     * @param {Object} overwriteParams Params für den Viewport-Tag, welche am Ende hinzugefügt werden
     */
    resetViewport(overwriteParams)
    {
        this.resizeViewport({siteWidth: 'device-width', upScalable: false, overwriteParams});
        return this;
    },

    setViewportHandlerOnOrientationChange()
    {
        throw new Error(
            'Please use "responder::createViewportResizer" to implement your own logic.'
        );
    },

    /** Generiert eine ViewportResizer-Instanz, mit der man beispielsweise bei einem OrientationChange den
     * Viewport anpassen kann. Die Instanz lässt den übergebenen Handler erst zu, nachdem der Viewport
     * zurückgesetzt wurde. Verhindert außerdem eine Resize-Schleife
     * @param {Function} handler Sollte ``responder::resizeViewport`` ausführen
     * @param {Object} overwriteParams Params für den {@link responder.resetViewport resetViewport-Aufruf}
     * @returns {ViewportResizer}
     */
    createViewportResizer(handler, overwriteParams)
    {
        return new ViewportResizer(handler, overwriteParams);
    },

    /** Nur über diese Factory-Methode sollte ein BreakpointController erstellt werden.
     * @param {Object.<string, number>} config ein Objekt mit Viewportgrößen: die Keys sind die Namen der Viewports,
     * die Attribute die minimale Weite in Pixeln. Die kleinste Größe sollte immer bei 0 beginnen. Z.B. `{xs: 0, md: 768}`
     * @param {boolean} [breakOnWindowLoaded=false] Fires first break event when window is loaded
     * @returns {BreakpointController}
     */
    createBreakpointController(config, breakOnWindowLoaded = false)
    {
        const viewports = [];
        for (const viewport in config)
        {
            viewports.push({name: viewport, minWidth: config[viewport]});
        }
        viewports.sort((a, b) => a.minWidth - b.minWidth);

        return new BreakpointController(viewports, breakOnWindowLoaded); // eslint-disable-line
    },

    /**  */
    setEnabled(e)
    {
        enabled = e;
    },

    /**  */
    triggerResize()
    {
        resizeHandler();
    },

    /**  */
    hasOverflowY()
    {
        return document.documentElement.scrollHeight > window.innerHeight;
    }
};

const responder = mix(methods, true).with(Eventable);

class BreakpointController extends mix().with(Eventable)
{
    /**
     * @mixes Eventable
     * @hideconstructor
     */
    constructor(viewports, breakOnWindowLoaded)
    {
        super();
        this._viewports = viewports;
        this._viewportsLength = viewports.length;
        /**
         * @memberof BreakpointController.prototype
         * @var {string} viewport Der aktuelle Viewport-Name.
         */
        this.currentViewport = '';
        this.viewport = '';
        if (windowLoaded || breakOnWindowLoaded !== true)
        {
            this._resizeHandler();
        }

        this._boundResizeHandler = this._resizeHandler.bind(this);
        responder.on('resize', this._boundResizeHandler);
    }

    _resizeHandler()
    {
        let viewport;
        let i = this._viewportsLength;

        while (i--)
        {
            viewport = this._viewports[i];
            if (responder.windowWidth >= viewport.minWidth)
            {
                break;
            }
        }

        if (viewport.name !== this.viewport)
        {
            const oldViewport = this.viewport;
            this.currentViewport = viewport.name;
            this.viewport = viewport.name;
            /**
             * Wird bei einem Wechsel des Viewports gefeuert.
             * @event BreakpointController#break
             * @property {string} viewport
             * @property {string} oldViewport
             */
            this.fire('break', {viewport: this.viewport, oldViewport});
        }
    }

    /** Ist der aktuelle Viewport größer als der übergebene?
     * @param {string} viewportName
     * @param {boolean=} returnLevel Wenn gesetzt, wird zurückgegeben, wieviele Viewport-Level der aktuelle und der
     * übergebene Viewport auseinanderliegen.
     * @return {boolean|number}
     */
    isViewportAbove(viewportName, returnLevel)
    {
        const len = this._viewportsLength;

        let searchAbove = false;
        let aboveLevel = 0;

        for (let i = 0; i < len; i++)
        {
            const viewport = this._viewports[i];
            if (searchAbove)
            {
                aboveLevel++;
                if (this.viewport === viewport.name)
                {
                    return returnLevel ? aboveLevel : true;
                }
            }
            if (viewport.name === viewportName)
            {
                searchAbove = true;
            }
        }

        return false;
    }

    /** Ist der aktuelle Viewport kleiner als der übergebene?
     * @param {string} viewportName
     * @param {boolean=} returnLevel Wenn gesetzt, wird zurückgegeben, wieviele Viewport-Level der aktuelle und der
     * übergebene Viewport auseinanderliegen.
     * @return {boolean|number}
     */
    isViewportBelow(viewportName, returnLevel)
    {
        let i = this._viewportsLength;
        let searchBelow = false;
        let belowLevel = 0;

        while (i--)
        {
            const viewport = this._viewports[i];
            if (searchBelow)
            {
                belowLevel++;
                if (this.viewport === viewport.name)
                {
                    return returnLevel ? belowLevel : true;
                }
            }
            if (viewport.name === viewportName)
            {
                searchBelow = true;
            }
        }

        return false;
    }

    /** Entfernt alle Event-Handler und entkoppelt den Controller vom
     * {@link responder#event:resize|resize-Event}
     */
    destroy()
    {
        this.removeAllListeners();
        responder.off('resize', this._boundResizeHandler);
        this._boundResizeHandler = null;
        this._viewports = null;
    }
}

window.addEventListener('resize', resizeHandler);
window.addEventListener('orientationchange', resizeHandler);
window.addEventListener('load', (evt) =>
{
    windowLoaded = true;
    resizeHandler(evt);
}, {once: true});

export default responder;
